import { access, mkdir, writeFile } from "node:fs/promises";
import { join } from "node:path";
import { cwd } from "node:process";
import {
  getAllTemplateMetadata,
  getFilledTemplates,
  type ProjectConfig,
  type TemplateType,
} from "@bmpr/templates";
import {
  cancel,
  confirm,
  intro,
  isCancel,
  outro,
  select,
  spinner,
  text,
} from "@clack/prompts";
import kleur from "kleur";
import { configureAgentTools } from "./integrations";

const { green, yellow, cyan, bold } = kleur;

const CLAUDE_COMMAND = `---
description: Manage project context with Bumper
argument-hint: "[read|update|complete|decide|focus|validate] [args...]"
---

Use the Bumper MCP tools to manage the project context. Available subcommands:

- read [section] - Read current project context (optionally filter to a section)
- update [content] - Update the project context
- complete [feature] - Mark a planned feature as completed
- decide [category] [name] [description] - Add an architectural decision
- focus [focus] [status] [blockers] - Update current work focus and status
- validate - Validate the context file for errors

Examples:
- /bumper read - Display full context
- /bumper complete User authentication - Mark feature complete
- /bumper decide Architecture Single File All state in one markdown file - Add decision
- /bumper focus API refactoring In progress Waiting for schema approval - Update work

Use the appropriate Bumper MCP tool based on the subcommand provided in $ARGUMENTS.
`;

const OPENCODE_COMMAND = `---
description: Manage project context with Bumper
template: |
  Use the Bumper MCP tools to manage the project context based on: $ARGUMENTS

  Available subcommands:
  - read [section] - Read current project context
  - update [content] - Update the project context
  - complete [feature] - Mark a planned feature as completed
  - decide [category] [name] [description] - Add an architectural decision
  - focus [focus] [status] [blockers] - Update current work focus and status
  - validate - Validate the context file for errors
agent: primary
---
`;

// fillTemplate and ProjectConfig are now imported from @bmpr/templates

async function fileExists(path: string): Promise<boolean> {
  try {
    await access(path);
    return true;
  } catch {
    return false;
  }
}

async function setupCLICommands(programs: Set<string>): Promise<void> {
  const currentDir = cwd();

  if (programs.has("claude-code")) {
    const claudeDir = join(currentDir, ".claude", "commands");
    await mkdir(claudeDir, { recursive: true });
    await writeFile(join(claudeDir, "bumper.md"), CLAUDE_COMMAND, "utf-8");
  }

  if (programs.has("opencode")) {
    const opencodeDir = join(currentDir, ".opencode", "command");
    await mkdir(opencodeDir, { recursive: true });
    await writeFile(join(opencodeDir, "bumper.md"), OPENCODE_COMMAND, "utf-8");
  }
}

export async function initCommand(args: string[] = []) {
  // Parse --cli flag
  const cliIndex = args.indexOf("--cli");
  const cliPrograms = new Set<string>();

  if (cliIndex !== -1 && cliIndex + 1 < args.length) {
    const nextArg = args[cliIndex + 1];
    if (nextArg === "claude-code" || nextArg === "opencode") {
      cliPrograms.add(nextArg);
    } else if (nextArg === "all") {
      cliPrograms.add("claude-code");
      cliPrograms.add("opencode");
    }
  }

  // Parse --template flag
  const templateIndex = args.indexOf("--template");
  let templateType: TemplateType | undefined;

  if (templateIndex !== -1 && templateIndex + 1 < args.length) {
    const templateArg = args[templateIndex + 1] as TemplateType;
    const validTemplates = ["default", "web-app", "cli-tool", "library"];
    if (validTemplates.includes(templateArg)) {
      templateType = templateArg;
    } else {
      console.error(
        `Invalid template type: ${templateArg}. Valid options: ${validTemplates.join(", ")}`,
      );
      process.exit(1);
    }
  }

  intro(cyan(bold("🚀 Initialize Bumper")));

  const s = spinner();

  try {
    // Template selection (if not provided via flag)
    if (!templateType) {
      const templates = getAllTemplateMetadata();
      const templateChoice = await select({
        message: "Select a project template:",
        options: templates.map((t) => ({
          value: t.type,
          label: t.name,
          hint: t.description,
        })),
        initialValue: "default",
      });

      if (isCancel(templateChoice)) {
        cancel("Initialization cancelled");
        process.exit(0);
      }

      templateType = templateChoice as TemplateType;
    }

    s.start("Gathering project information");

    const name = await text({
      message: "Project name:",
      placeholder: "my-awesome-project",
      validate: (value) => (value.length > 0 ? undefined : "Name is required"),
    });

    if (isCancel(name)) {
      cancel("Initialization cancelled");
      process.exit(0);
    }

    const description = await text({
      message: "Brief description:",
      placeholder: "What does this project do?",
      validate: (value) =>
        value.length > 0 ? undefined : "Description is required",
    });

    if (isCancel(description)) {
      cancel("Initialization cancelled");
      process.exit(0);
    }

    const language = await text({
      message: "Language:",
      placeholder: "TypeScript, Go, Python, etc.",
      initialValue: "TypeScript",
    });

    if (isCancel(language)) {
      cancel("Initialization cancelled");
      process.exit(0);
    }

    const framework = await text({
      message: "Framework (or 'none'):",
      placeholder: "React, Next.js, FastAPI, etc.",
      initialValue: "none",
    });

    if (isCancel(framework)) {
      cancel("Initialization cancelled");
      process.exit(0);
    }

    const database = await text({
      message: "Database (or 'none'):",
      placeholder: "PostgreSQL, SQLite, MongoDB, etc.",
      initialValue: "none",
    });

    if (isCancel(database)) {
      cancel("Initialization cancelled");
      process.exit(0);
    }

    const config: ProjectConfig = {
      name: String(name),
      description: String(description),
      language: String(language),
      framework: String(framework),
      database: String(database),
    };
    s.stop();

    // Check if .bumper already exists
    const bumperDir = join(cwd(), ".bumper");
    const bumperExists = await fileExists(bumperDir);

    if (bumperExists) {
      const contextPath = join(bumperDir, "context.md");
      const contextExists = await fileExists(contextPath);
      if (contextExists) {
        // Bumper is already initialized - offer to configure agent tools
        console.log("");
        console.log(green("✓ Bumper is already initialized in this project!"));
        console.log("");

        const shouldConfigureTools = await confirm({
          message: "Would you like to configure agent tool integrations?",
          initialValue: true,
        });

        if (isCancel(shouldConfigureTools) || !shouldConfigureTools) {
          cancel("Operation cancelled.");
          process.exit(0);
        }

        // Just configure agent tools
        const { configured, files } = await configureAgentTools(cwd());

        // Build output message
        const outputLines = [
          "",
          green("✓ Agent tool configuration complete!"),
          "",
        ];

        if (files.length > 0) {
          outputLines.push("Configured auto-load in:");
          for (const file of files) {
            outputLines.push(`  ${file}`);
          }
          outputLines.push("");
        }

        if (configured.length > 0) {
          outputLines.push(
            `🎉 Auto-load configured for: ${configured.join(", ")}`,
          );
          outputLines.push(
            "Bumper context will load automatically in all future sessions!",
          );
        } else {
          outputLines.push(
            "💡 No agent tools detected. Bumper can auto-configure for:",
          );
          outputLines.push("   • Claude Code (via CLAUDE.md)");
          outputLines.push("   • OpenCode (via opencode.json or AGENTS.md)");
          outputLines.push("");
          outputLines.push(
            "   Create one of these files and re-run 'bmpr init'",
          );
        }

        outputLines.push("");

        outro(outputLines.join("\n"));
        return;
      }
    }

    // Create .bumper directory
    const s2 = spinner();
    s2.start("Creating .bumper directory");

    await mkdir(bumperDir, { recursive: true });

    // Fill and write templates
    const templates = getFilledTemplates(config, templateType);

    await writeFile(join(bumperDir, "LLM.md"), templates.llm, "utf-8");
    await writeFile(join(bumperDir, "context.md"), templates.context, "utf-8");

    // Setup CLI commands if specified
    if (cliPrograms.size > 0) {
      s2.stop();
      const s3 = spinner();
      s3.start("Setting up CLI commands");
      await setupCLICommands(cliPrograms);
      s3.stop();
    } else {
      s2.stop();
    }

    // Auto-configure agent tools for auto-loading
    const { configured, files } = await configureAgentTools(cwd());

    // Build output message
    const outputLines = [
      green("✓ Bumper initialized successfully!"),
      "",
      "Created files:",
      `  ${cyan(".bumper/LLM.md")} - AI instructions`,
      `  ${cyan(".bumper/context.md")} - Project memory`,
    ];

    if (cliPrograms.has("claude-code")) {
      outputLines.push(
        "  " +
          cyan(".claude/commands/bumper.md") +
          " - Claude Code slash command",
      );
    }

    if (cliPrograms.has("opencode")) {
      outputLines.push(
        `  ${cyan(".opencode/command/bumper.md")} - OpenCode slash command`,
      );
    }

    if (files.length > 0) {
      outputLines.push("");
      outputLines.push("Configured auto-load in:");
      for (const file of files) {
        outputLines.push(`  ${file}`);
      }
    }

    outputLines.push("");

    if (configured.length > 0) {
      outputLines.push(`🎉 Auto-load configured for: ${configured.join(", ")}`);
      outputLines.push(
        "Bumper context will load automatically in all future sessions!",
      );
    } else {
      outputLines.push(
        "💡 No agent tools detected. Bumper can auto-configure for:",
      );
      outputLines.push("   • Claude Code (via CLAUDE.md)");
      outputLines.push("   • OpenCode (via opencode.json or AGENTS.md)");
      outputLines.push("");
      outputLines.push("   Create one of these files and re-run 'bmpr init'");
    }

    outputLines.push(
      "",
      "Next steps:",
      "  1. Review " +
        cyan(".bumper/context.md") +
        " and customize for your project",
      "  2. Commit to git: " +
        yellow("git add .bumper/ && git commit -m 'Initialize Bumper'"),
    );

    if (configured.length > 0) {
      outputLines.push(
        "  3. Start coding - Bumper context loads automatically!",
      );
    } else {
      outputLines.push(
        "  3. Tell your AI: " +
          yellow("Read .bumper/LLM.md and .bumper/context.md"),
      );
    }

    outputLines.push("");

    outro(outputLines.join("\n"));
  } catch (error) {
    s.stop();
    cancel("Initialization failed");
    if (error instanceof Error) {
      console.error("Error:", error.message);
    }
    process.exit(1);
  }
}
